/**
 * v-resize directive: calls the bound function with a ResizeObserverEntry
 * whenever the element's size changes.
 *
 * <div v-resize="onResize">                 every change
 * <div v-resize:debounce.100="onResize">    debounced 100 ms (default 150)
 */

import type { App, Directive, DirectiveBinding } from 'vue';

type ResizeCallback = (entry: ResizeObserverEntry) => void;

interface ResizeDirectiveState {
   observer: ResizeObserver;
   callback: ResizeCallback;
   debounceTimer?: ReturnType<typeof setTimeout>;
}

// Observer state per bound element
const observerMap = new WeakMap<HTMLElement, ResizeDirectiveState>();

/** Return a callback that runs `callback` once `delay` ms pass without another call. */
function createDebouncedCallback(
   callback: ResizeCallback,
   delay: number,
   state: ResizeDirectiveState,
): ResizeCallback {
   return (entry: ResizeObserverEntry) => {
      if (state.debounceTimer) {
         clearTimeout(state.debounceTimer);
      }
      state.debounceTimer = setTimeout(() => {
         callback(entry);
      }, delay);
   };
}

/**
 * Return the debounce delay in ms from `:debounce.<ms>`; 150 when no value is
 * given, null when the argument is not `debounce`.
 */
function getDebounceDelay(binding: DirectiveBinding): number | null {
   if (binding.arg !== 'debounce') {
      return null;
   }

   // Modifiers arrive as keys: .100 becomes {'100': true}
   const modifierKeys = Object.keys(binding.modifiers);
   for (const key of modifierKeys) {
      const delay = Number.parseInt(key, 10);
      if (!Number.isNaN(delay) && delay > 0) {
         return delay;
      }
   }

   return 150;
}

/**
 * Observe the element, replacing any existing observer. Warns and does nothing
 * when the bound value is not a function.
 */
function setupObserver(el: HTMLElement, binding: DirectiveBinding<ResizeCallback>): void {
   cleanupObserver(el);

   const callback = binding.value;
   if (typeof callback !== 'function') {
      console.warn('[v-resize] Directive value must be a function');
      return;
   }

   const debounceDelay = getDebounceDelay(binding);

   // The debounced callback stores its timer on this state
   const state: ResizeDirectiveState = {
      observer: null as unknown as ResizeObserver,
      callback,
   };

   const actualCallback =
      debounceDelay !== null
         ? createDebouncedCallback(callback, debounceDelay, state)
         : callback;

   const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
         actualCallback(entry);
      }
   });

   state.observer = observer;
   observerMap.set(el, state);

   observer.observe(el);
}

/** Disconnect the element's observer and pending debounce timer; no-op when none. */
function cleanupObserver(el: HTMLElement): void {
   const state = observerMap.get(el);
   if (state) {
      if (state.debounceTimer) {
         clearTimeout(state.debounceTimer);
      }
      state.observer.disconnect();
      observerMap.delete(el);
   }
}

export const resizeDirective: Directive<HTMLElement, ResizeCallback> = {
   mounted(el: HTMLElement, binding: DirectiveBinding<ResizeCallback>) {
      setupObserver(el, binding);
   },

   updated(el: HTMLElement, binding: DirectiveBinding<ResizeCallback>) {
      // Only recreate if the callback changed
      const state = observerMap.get(el);
      if (state && state.callback !== binding.value) {
         setupObserver(el, binding);
      }
   },

   unmounted(el: HTMLElement) {
      cleanupObserver(el);
   },
};

/** Register the directive globally as `v-resize`. */
export function installResize(app: App): void {
   app.directive('resize', resizeDirective);
}
